import { Component, ElementRef, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { SitesService } from 'src/app/sites/sites.service';

type DurationUnit = 'minutes' | 'hours';

/**
 * Non-duration settings can still show a unit suffix without implying a seconds conversion —
 * the save loop only scales 'hours'/'minutes', so any other unit falls through to the raw value.
 */
type DisplayUnit = DurationUnit | 'batteries';

/**
 * A row on the tab. `options` present means the setting renders as a dropdown of those
 * values; absent means the default duration number input.
 */
interface BrmSettingField {
	name: string;
	label: string;
	tooltip: string;
	options?: number[];
}

/**
 * `unit` is absent for settings with no unit to show. A non-duration `unit` (e.g. 'batteries')
 * still renders a suffix but is saved and loaded as entered, with no seconds conversion.
 */
interface BrmSettingRange {
	min: number;
	max: number;
	unit?: DisplayUnit;
}

@Component({
	selector: 'app-brm-site-settings',
	templateUrl: './brm-site-settings.component.html',
	styleUrls: ['./brm-site-settings.component.css']
})
export class BrmSiteSettingsComponent {
	// Indexed by field name from the template and the save/validate loops, so it carries an
	// index signature rather than a fixed literal shape.
	brmSiteSettings: { [name: string]: number } = {
		relaxing_time_period: 4,
		buzzer_interval: 10,
		max_battery_refresh_time: 10,
		min_potentially_malfunctioned_duration: 10,
		ready_to_pick_queue_count: 1,
	};

	// tooltip holds the key under the `tooltip.` namespace — app-help-tooltip prefixes and translates it.
	fields: BrmSettingField[] = [
		{
			name: 'relaxing_time_period',
			label: this.translationService.instant('brm_site_settings.relaxing_time_period'),
			tooltip: 'brm_site_settings_relaxing_time_period',
		},
		{
			name: 'buzzer_interval',
			label: this.translationService.instant('brm_site_settings.buzzer_interval'),
			tooltip: 'brm_site_settings_buzzer_interval',
		},
		{
			name: 'max_battery_refresh_time',
			label: this.translationService.instant('brm_site_settings.max_battery_refresh_time'),
			tooltip: 'brm_site_settings_max_battery_refresh_time',
		},
		{
			name: 'min_potentially_malfunctioned_duration',
			label: this.translationService.instant('brm_site_settings.min_potentially_malfunctioned_duration'),
			tooltip: 'brm_site_settings_min_potentially_malfunctioned_duration',
		},
		{
			name: 'ready_to_pick_queue_count',
			label: this.translationService.instant('brm_site_settings.ready_to_pick_queue_count'),
			tooltip: 'brm_site_settings_ready_to_pick_queue_count',
			options: [1, 2],
		},
	];

	fieldRanges: { [name: string]: BrmSettingRange } = {
		relaxing_time_period: { min: 0, max: 24 , unit: 'hours' as DurationUnit },
		buzzer_interval: { min: 0, max: 60 , unit: 'minutes' as DurationUnit },
		max_battery_refresh_time: { min: 0, max: 1440 , unit: 'minutes' as DurationUnit },
		min_potentially_malfunctioned_duration: { min: 0, max: 1440 , unit: 'minutes' as DurationUnit },
		ready_to_pick_queue_count: { min: 1, max: 2, unit: 'batteries' as DisplayUnit },
	};

	disableButton: boolean = false;
	siteId: number;

	constructor(
		private translationService: TranslateService,
		private siteService: SitesService,
		private elementRef: ElementRef,
		private render: Renderer2,
		private route: ActivatedRoute,
		private notificationService: NotificationMessageService,
	) {}

	ngOnInit() {
		this.siteId = Number(this.route.parent.snapshot.params.siteId);

		this.siteService.getBrmSiteSettings(this.siteId).subscribe((data:any) => {
			if (data) {
				this.brmSiteSettings = {
					relaxing_time_period: data.relaxing_time_period / 3600,
					buzzer_interval: data.buzzer_interval / 60,
					max_battery_refresh_time: data.max_battery_refresh_time / 60,
					min_potentially_malfunctioned_duration: data.min_potentially_malfunctioned_duration / 60,
					// A count, not a duration — loaded as stored. Falls back to the default when the
					// column is missing, so the tab still works before the release query has run.
					ready_to_pick_queue_count: +data.ready_to_pick_queue_count || 1,
				};
			} else {
				this.notificationService.setMessage('globalErrMsg');
			}
		});
	}

	saveBatteryUtilizationSettings() {
		const invalidInputs = this.elementRef.nativeElement.querySelectorAll('.invalid-input');
		invalidInputs.forEach((item) => {
			item.classList.remove('invalid-input');
		});

		this.validateInputs();

		const settings = {};

		for (const field in this.brmSiteSettings) {
			const newValue = this.brmSiteSettings[field];

			const unit = this.fieldRanges[field].unit;

			if (unit === 'hours') {
				settings[field] = newValue * 60 * 60;
			} else if (unit === 'minutes') {
				settings[field] = newValue * 60;
			} else {
				settings[field] = newValue; // unitless count — persisted exactly as selected
			}
		}

		this.siteService.saveBrmSiteSettings(this.siteId, settings).subscribe((res: any) => {
			if (res) {
				return this.notificationService.setMessage('globalSuccessMsg');
			} else {
				return this.notificationService.setMessage('globalErrMsg');
			}
		});
	}

	validateInputs() {
		this.disableButton = false;

		for (const field in this.brmSiteSettings) {
			const newValue = this.brmSiteSettings[field];
			const ele = this.elementRef.nativeElement.querySelector(`#${field}`);
			this.render.removeClass(ele, 'invalid-input');

			if (newValue == null || newValue == undefined || isNaN(newValue)) {
				this.handleInvalidInputs(field);
				continue;
			}

			if (newValue <= 0 || newValue > this.fieldRanges[field].max)
				this.handleInvalidInputs(field);
		}
	}

	handleInvalidInputs(field: string): boolean {
		const ele = this.elementRef.nativeElement.querySelector(`#${field}`);

		if(ele)
			this.render.addClass(ele, 'invalid-input');

		return this.disableButton = true;
	}
}
